/**
 * Hook utilitário para seleção avançada de itens
 * @param {Array} items - Array de itens disponíveis para seleção
 * @returns {Object} Objeto com estados e funções de seleção avançada
 * 
 * MELHORIAS IMPLEMENTADAS:
 * - Seleção por critérios (não lidos, favoritos, sentimento, etc.)
 * - Seleção inversa
 * - Seleção por range
 * - Histórico de seleções
 * - Estatísticas de seleção
 */
import { useState, useCallback, useMemo } from 'react';

const useAdvancedSelection = (items = []) => {
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectionHistory, setSelectionHistory] = useState([]);

  // Toggle selection mode
  const toggleSelectionMode = useCallback(() => {
    setSelectionMode(prev => !prev);
    if (selectionMode) {
      // Clear selections when disabling
      setSelectedItems([]);
      setSelectionHistory([]);
    }
  }, [selectionMode]);

  // Toggle selection of a single item
  const toggleItemSelection = useCallback((itemId) => {
    // Se não está em modo seleção, ativar automaticamente (para Ctrl+Click)
    if (!selectionMode) {
      setSelectionMode(true);
    }

    setSelectedItems(prev => {
      const newSelection = prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId];

      // Adicionar ao histórico
      setSelectionHistory(history => [...history.slice(-9), {
        action: 'toggle',
        itemId,
        timestamp: Date.now(),
        totalSelected: newSelection.length
      }]);

      return newSelection;
    });
  }, [selectionMode]);

  // Select all items
  const selectAll = useCallback(() => {
    const allIds = items.map(item => item._id || item.id);
    setSelectedItems(allIds);
    setSelectionHistory(history => [...history.slice(-9), {
      action: 'selectAll',
      timestamp: Date.now(),
      totalSelected: allIds.length
    }]);
  }, [items]);
  
  // Select items by criteria
  const selectByCriteria = useCallback((criteria) => {
    const criteriaIds = items
      .filter(item => {
        switch (criteria) {
          case 'unread':
            return !item.isRead;
          case 'read':
            return item.isRead;
          case 'favorite':
            return item.isFavorite;
          case 'not_favorite':
            return !item.isFavorite;
          case 'positive':
            return item.sentiment === 'positive';
          case 'neutral':
            return item.sentiment === 'neutral';
          case 'negative':
            return item.sentiment === 'negative';
          case 'with_image':
            return item.image || item.imageUrl;
          case 'without_image':
            return !item.image && !item.imageUrl;
          case 'with_tags':
            return item.tags && item.tags.length > 0;
          case 'no_tags':
            return !item.tags || item.tags.length === 0;
          case 'recent':
            const oneWeekAgo = new Date();
            oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
            return new Date(item.date || item.createdAt) > oneWeekAgo;
          case 'old':
            const oneMonthAgo = new Date();
            oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
            return new Date(item.date || item.createdAt) < oneMonthAgo;
          default:
            return false;
        }
      })
      .map(item => item._id || item.id);
    
    setSelectedItems(criteriaIds);
    setSelectionHistory(history => [...history.slice(-9), {
      action: 'selectByCriteria',
      criteria,
      timestamp: Date.now(),
      totalSelected: criteriaIds.length
    }]);
  }, [items]);

  // Select items by group function
  const selectByGroup = useCallback((groupFn) => {
    const groupIds = items
      .filter(groupFn)
      .map(item => item._id || item.id);
    
    setSelectedItems(groupIds);
    setSelectionHistory(history => [...history.slice(-9), {
      action: 'selectByGroup',
      timestamp: Date.now(),
      totalSelected: groupIds.length
    }]);
  }, [items]);

  // Select by range (first to last)
  const selectByRange = useCallback((startIndex, endIndex) => {
    const rangeItems = items.slice(startIndex, endIndex + 1);
    const rangeIds = rangeItems.map(item => item._id || item.id);
    
    setSelectedItems(rangeIds);
    setSelectionHistory(history => [...history.slice(-9), {
      action: 'selectByRange',
      startIndex,
      endIndex,
      timestamp: Date.now(),
      totalSelected: rangeIds.length
    }]);
  }, [items]);

  // Inverse selection
  const selectInverse = useCallback(() => {
    const allIds = items.map(item => item._id || item.id);
    const inverseIds = allIds.filter(id => !selectedItems.includes(id));
    
    setSelectedItems(inverseIds);
    setSelectionHistory(history => [...history.slice(-9), {
      action: 'selectInverse',
      timestamp: Date.now(),
      totalSelected: inverseIds.length
    }]);
  }, [items, selectedItems]);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedItems([]);
    setSelectionHistory(history => [...history.slice(-9), {
      action: 'clearSelection',
      timestamp: Date.now(),
      totalSelected: 0
    }]);
  }, []);

  // Clean up selection by removing IDs that no longer exist in items
  const cleanupSelection = useCallback((validIds = null) => {
    const idsToCheck = validIds || items.map(item => item._id || item.id);
    
    setSelectedItems(prev => {
      const cleanedSelection = prev.filter(id => idsToCheck.includes(id));
      
      if (cleanedSelection.length !== prev.length) {
        // Se houve mudança, adicionar ao histórico
        setSelectionHistory(history => [...history.slice(-9), {
          action: 'cleanup',
          timestamp: Date.now(),
          totalSelected: cleanedSelection.length,
          removedCount: prev.length - cleanedSelection.length
        }]);
      }
      return cleanedSelection;
    });
  }, [items, selectedItems]);

  // Check if an item is selected
  const isSelected = useCallback((itemId) => {
    return selectedItems.includes(itemId);
  }, [selectedItems]);

  // Get selection statistics
  const selectionStats = useMemo(() => {
    const selectedItemsData = items.filter(item => 
      selectedItems.includes(item._id || item.id)
    );

    return {
      total: selectedItemsData.length,
      read: selectedItemsData.filter(item => item.isRead).length,
      unread: selectedItemsData.filter(item => !item.isRead).length,
      favorite: selectedItemsData.filter(item => item.isFavorite).length,
      notFavorite: selectedItemsData.filter(item => !item.isFavorite).length,
      positive: selectedItemsData.filter(item => item.sentiment === 'positive').length,
      neutral: selectedItemsData.filter(item => item.sentiment === 'neutral').length,
      negative: selectedItemsData.filter(item => item.sentiment === 'negative').length,
      withImage: selectedItemsData.filter(item => item.image || item.imageUrl).length,
      withoutImage: selectedItemsData.filter(item => !item.image && !item.imageUrl).length,
      withTags: selectedItemsData.filter(item => item.tags && item.tags.length > 0).length,
      noTags: selectedItemsData.filter(item => !item.tags || item.tags.length === 0).length
    };
  }, [items, selectedItems]);

  // Get quick selection options
  const quickSelectionOptions = useMemo(() => [
    { label: 'Não Lidos', value: 'unread', count: items.filter(item => !item.isRead).length },
    { label: 'Lidos', value: 'read', count: items.filter(item => item.isRead).length },
    { label: 'Favoritos', value: 'favorite', count: items.filter(item => item.isFavorite).length },
    { label: 'Positivos', value: 'positive', count: items.filter(item => item.sentiment === 'positive').length },
    { label: 'Com Imagem', value: 'with_image', count: items.filter(item => item.image || item.imageUrl).length },
    { label: 'Com Tags', value: 'with_tags', count: items.filter(item => item.tags && item.tags.length > 0).length },
    { label: 'Recentes (1 semana)', value: 'recent', count: items.filter(item => {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      return new Date(item.date || item.createdAt) > oneWeekAgo;
    }).length }
  ], [items]);

  // Undo last selection action
  const undoLastAction = useCallback(() => {
    if (selectionHistory.length === 0) return;
    
    const lastAction = selectionHistory[selectionHistory.length - 1];
    const previousHistory = selectionHistory.slice(0, -1);
    
    // Restaurar seleção anterior (simplificado)
    if (previousHistory.length > 0) {
      const previousAction = previousHistory[previousHistory.length - 1];
      // Aqui você poderia implementar uma lógica mais complexa para restaurar o estado exato
      setSelectedItems([]);
    } else {
      setSelectedItems([]);
    }
    
    setSelectionHistory(previousHistory);
  }, [selectionHistory]);

  return {
    // Estados
    selectionMode,
    selectedItems,
    selectionHistory,
    
    // Funções básicas
    setSelectedItems,
    toggleSelectionMode,
    toggleItemSelection,
    selectAll,
    clearSelection,
    cleanupSelection,
    isSelected,
    
    // Funções avançadas
    selectByCriteria,
    selectByGroup,
    selectByRange,
    selectInverse,
    undoLastAction,
    
    // Estatísticas e informações
    selectionStats,
    quickSelectionOptions,
    
    // Utilitários
    hasSelection: selectedItems.length > 0,
    isAllSelected: selectedItems.length === items.length && items.length > 0,
    isPartiallySelected: selectedItems.length > 0 && selectedItems.length < items.length
  };
};

export default useAdvancedSelection;